import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, Observable } from 'rxjs';
import { UnsavedChangesDialog } from '../../unsaved-changes-dialog/unsaved-changes-dialog';
import { TemplateConfirmDialog } from '../../template-confirm-dialog/template-confirm-dialog';
import {
  ConfirmActionData,
  ConfirmDialog,
  ConfirmDialogData,
} from '../../confirm-dialog/confirm-dialog';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly dialog = inject(MatDialog);

  openConfirmDeleteDialog(data: ConfirmDialogData, onConfirm: () => void): void {
    this.openConfirmDialog(
      {
        title: data.title,
        icon: 'delete',
        message: data.message1,
        resourceName: data.resourceName,
        messageAfter: data.message2,
        confirmLabel: 'Smazat',
        destructive: true,
      },
      onConfirm,
    );
  }

  openConfirmDialog(data: ConfirmActionData, onConfirm: () => void): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data,
      width: '440px',
      restoreFocus: false,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        onConfirm();
      }
    });
  }

  // Reminds the user that a pre-written template always has to be adapted to
  // their own school before it is inserted into a rich-text field. `replace`
  // tells the caller whether to overwrite the field or append to it. `note`
  // adds field-specific instructions to the standard text.
  openTemplateConfirmDialog(
    fieldHasContent: boolean,
    onConfirm: (replace: boolean) => void,
    note?: string,
  ): void {
    const dialogRef = this.dialog.open(TemplateConfirmDialog, {
      data: { fieldHasContent, note },
      width: '560px',
      restoreFocus: false,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result: { replace: boolean } | null) => {
      if (result) {
        onConfirm(result.replace);
      }
    });
  }

  // Prompts the user before discarding unsaved changes. Emits `true` to leave
  // (discard), `false` to stay. Used by the unsaved-changes route guard.
  openUnsavedChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialog.open(UnsavedChangesDialog, {
      width: '400px',
      restoreFocus: false,
      autoFocus: false,
    });

    return dialogRef.afterClosed().pipe(map((leave) => leave === true));
  }
}
